// @flow

import * as React from 'react';

import { NavigableListItem } from '@ta/list.navigable-list';

import type { FetcherResult } from '../Typeahead';
import BasicResult, { type PaddingStyle } from './BasicResult';
import TypeaheadTracking, { type UserInteractionType } from '../TypeaheadTracking';
import type { TypeaheadResult } from '../TypeaheadResult';
import type { AddressDisplay } from './addressDisplay';
import formatAddress from './addressDisplay';

// /recent API result type (RecentlyViewedFetcher.RecentlyViewedType) to typeahead type from UnifiedLookupHandler.ReturnTypes
const RECENTLY_VIEWED_TO_TYPEAHEAD_TYPE = {
  geos: 'GEO',
  neighborhoods: 'NEIGHBORHOOD',
  attractions: 'ATTRACTION',
  hotels: 'HOTEL',
  vr: 'VACATION_RENTAL',
  restaurants: 'EATERY',
  airports: 'AIRPORT',
  airlines: 'AIRLINE',
  cruise_lines: 'CRUISE_LINE',
  attraction_products: 'ACTIVITY',
};

type Props = {|
  /** The RecentlyViewedFetcher result */
  result: FetcherResult,

  /** The typeahead tracker */
  tracker: TypeaheadTracking,

  /** An optional callback if a result is selected */
  onSelection?: (
    event: SyntheticEvent<HTMLElement> | KeyboardEvent,
    result: TypeaheadResult,
  ) => void,

  /** An optional callback for if/when the mouse moves over the result */
  onMouseMove?: (event: SyntheticMouseEvent<HTMLElement>) => mixed,

  /** True if this result is active. Defaults to false */
  active?: boolean,

  /** True if we should include an icon to match the result type */
  includeIcon?: boolean,

  /** Padding style for the result */
  paddingStyle?: PaddingStyle,

  /** Display mode for address subtitle of results.
   * If no display mode is provided, there will be no address displayed in subtitle */
  addressDisplay: AddressDisplay,

  /** result position in result list */
  resultIndex?: number,
|};

/**
 * Create a typeahead search result from a recently viewed API result. Selecting a result of this type will trigger a
 * call to onSelection() with a TypeaheadResult of { type: "recent_location_id", value: <geo_id> }
 *
 * Note that this is intended to be used inside a <NavigableList/>
 */
class RecentlyViewedResult extends React.Component<Props> {
  static defaultProps = {
    onSelection: undefined,
    onMouseMove: undefined,
    active: false,
    includeIcon: false,
    paddingStyle: undefined,
    addressDisplay: 'parent_geos',
    resultIndex: undefined,
  };

  onSelect = (event: SyntheticEvent<HTMLElement> | KeyboardEvent) => {
    const userInteractionType: UserInteractionType = event.type === 'click' ? 'click' : 'enter';
    const { tracker, onSelection, result, resultIndex } = this.props;
    const { location_id: geoId, resultType } = this.props.result;

    tracker.trackSelection(
      {
        document_id: geoId,
        data_type: 'LOCATION',
        fromHistory: true,
        type: RECENTLY_VIEWED_TO_TYPEAHEAD_TYPE[resultType] || resultType,
        resultIndex,
        ...result,
      },
      userInteractionType,
    );

    if (onSelection) {
      onSelection(event, {
        type: 'recent_location_id',
        value: geoId,
      });
    }
  };

  iconFromCategoryKey = (category: string) => {
    switch (category) {
      case 'attraction':
        return 'attractions';
      case 'hotel':
        return 'hotels';
      case 'restaurant':
        return 'restaurants';
      case 'geographic':
      default:
        return 'map-pin-fill';
    }
  };

  render() {
    const { result } = this.props;
    const { name: title, category, ancestors = [] } = result;

    const subtitle = formatAddress(
      this.props.addressDisplay,
      ancestors[0]?.name,
      ancestors[1]?.name,
      // eslint-disable-next-line camelcase
      result?.address_obj?.street1,
    );

    const iconName = this.props.includeIcon
      ? this.iconFromCategoryKey((category && category.key) || '')
      : '';

    return (
      <NavigableListItem
        active={!!this.props.active}
        onMouseMove={this.props.onMouseMove}
        onSelection={this.onSelect}
      >
        <BasicResult
          title={title}
          subtitle={subtitle}
          iconName={iconName}
          active={this.props.active}
          paddingStyle={this.props.paddingStyle}
        />
      </NavigableListItem>
    );
  }
}

export default RecentlyViewedResult;
